import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { get, post } from '../../../services/api';
import { useToast } from '../../../components';
import { useLang } from '../../../i18n';

export type ChannelKind = 'telegram' | 'whatsapp' | 'phone' | 'email';

export interface ServiceOption {
  value: string;
  label: string;
}

export interface UseLeadFormArgs {
  open: boolean;
  onClose: () => void;
  defaultServiceSlug?: string;
}

const MIN_FILL_MS = 2000;
const MIN_NAME = 2;
const MIN_DESC = 20;

function maskUzPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').replace(/^998/, '').slice(0, 9);
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 5);
  const p3 = digits.slice(5, 7);
  const p4 = digits.slice(7, 9);
  let out = '+998';
  if (p1) out += ` ${p1}`;
  if (p2) out += ` ${p2}`;
  if (p3) out += ` ${p3}`;
  if (p4) out += ` ${p4}`;
  return out;
}

function validateChannel(kind: ChannelKind, value: string): string | null {
  const v = value.trim();
  if (!v) return 'required';
  if (kind === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'invalid';
  if (kind === 'phone' || kind === 'whatsapp') {
    const digits = v.replace(/\D/g, '');
    return digits.length === 12 && digits.startsWith('998') ? null : 'invalid';
  }
  if (kind === 'telegram') {
    return /^@?[a-zA-Z0-9_]{4,}$/.test(v) || /^https?:\/\/t\.me\/[a-zA-Z0-9_]{4,}$/.test(v) ? null : 'invalid';
  }
  return null;
}

function backendLocale(lang: string): string {
  if (lang === 'uz-Latn') return 'uz_lat';
  if (lang === 'uz-Cyrl') return 'uz_cyr';
  if (lang === 'ru') return 'ru';
  return 'en';
}

export function useLeadForm({ open, onClose, defaultServiceSlug }: UseLeadFormArgs) {
  const { t, lang } = useLang();
  const toast = useToast();
  const startRef = useRef<number>(Date.now());

  const [name, setName] = useState('');
  // service хранит то, что пользователь ввёл/выбрал — может быть slug известной услуги
  // или произвольный текст. На бэк отдаём как service_slug (бэк сам решит, что делать).
  const [service, setService] = useState<string>('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState<string | null>(null);
  const [channels, setChannels] = useState<ChannelKind[]>([]);
  const [values, setValues] = useState<Record<ChannelKind, string>>({ telegram: '', whatsapp: '', phone: '', email: '' });
  const [agree, setAgree] = useState(false);
  const [honey, setHoney] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    if (!open) return;
    startRef.current = Date.now();
    setSubmitted(false);
    setErrors({});
  }, [open]);

  useEffect(() => {
    if (defaultServiceSlug) setService(defaultServiceSlug);
  }, [defaultServiceSlug]);

  useEffect(() => {
    let cancel = false;
    type Item = { slug: string; title?: string };
    type Resp = {
      items?: Item[];
      results?: Item[];
      data?: Item[];
    } | Item[];
    (async () => {
      try {
        const res = await get<Resp>(`/services/?lang=${backendLocale(lang)}&page_size=200`);
        if (cancel) return;
        const items: Item[] = Array.isArray(res)
          ? res
          : (res?.results || res?.items || res?.data || []);
        setServices(
          items
            .filter((s) => s && s.slug)
            .map((s) => ({ value: s.slug, label: (s.title || s.slug).trim() })),
        );
      } catch {
        if (!cancel) setServices([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  const toggleChannel = useCallback((kind: ChannelKind) => {
    setChannels((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));
  }, []);

  const setChannelValue = useCallback((kind: ChannelKind, raw: string) => {
    const next = kind === 'phone' || kind === 'whatsapp' ? maskUzPhone(raw) : raw;
    setValues((prev) => ({ ...prev, [kind]: next }));
  }, []);

  const budgetOptions = useMemo(() => [
    { value: 's', label: t('lead.budget.s') },
    { value: 'm', label: t('lead.budget.m') },
    { value: 'l', label: t('lead.budget.l') },
    { value: 'xl', label: t('lead.budget.xl') },
    { value: 'unsure', label: t('lead.budget.unsure') },
  ], [t]);

  const submit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (honey) { onClose(); return; }
    const elapsed = Date.now() - startRef.current;
    if (elapsed < MIN_FILL_MS) return;

    const nextErrors: Record<string, string> = {};
    if (name.trim().length < MIN_NAME) nextErrors.name = t('lead.err.name');
    if (description.trim().length < MIN_DESC) nextErrors.description = t('lead.err.desc');
    if (!agree) nextErrors.agree = t('lead.err.agree');
    if (channels.length === 0) {
      toast.error(t('lead.err.channels'));
      setErrors(nextErrors);
      return;
    }
    for (const k of channels) {
      const v = validateChannel(k, values[k]);
      if (v) nextErrors[`ch_${k}`] = t(v === 'required' ? 'lead.err.required' : 'lead.err.invalid');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      // Если введённый текст совпадает с label известной услуги — отдаём её slug,
      // иначе как свободный текст (бэк положит в referral_source).
      const serviceTrim = service.trim();
      const matched = services.find(
        (s) => s.label.toLowerCase() === serviceTrim.toLowerCase() || s.value === serviceTrim,
      );
      const serviceSlug = matched ? matched.value : serviceTrim;
      await post('/leads/', {
        name: name.trim(),
        service_slug: serviceSlug,
        description: description.trim(),
        budget_band: budget || '',
        contact_telegram: channels.includes('telegram') ? values.telegram.trim() : '',
        contact_phone: channels.includes('phone') ? values.phone.trim() : '',
        contact_whatsapp: channels.includes('whatsapp') ? values.whatsapp.trim() : '',
        contact_email: channels.includes('email') ? values.email.trim() : '',
        preferred_channel: channels.length === 1 ? channels[0] : channels.length > 1 ? channels[0] : 'any',
        website: '',
        time_to_fill_ms: elapsed,
      });
      setSubmitted(true);
      toast.success(t('lead.success.t'));
    } catch {
      toast.error(t('contacts.form.error'));
    } finally {
      setLoading(false);
    }
  }, [agree, budget, channels, description, honey, name, onClose, service, services, t, toast, values]);

  return {
    name, setName,
    service, setService,
    description, setDescription,
    budget, setBudget,
    channels, toggleChannel,
    values, setChannelValue,
    agree, setAgree,
    honey, setHoney,
    loading, submitted,
    services, budgetOptions,
    errors, submit,
  };
}
